import type { Swiper } from 'swiper';
import { ProductFilter, ProductSort } from 'types/schema';

export const MAX_PRODUCTS = 50;
export const INITIAL_PAGE_SIZE = 10;
export const MAX_FARM_TO_BIZ = 24;

const TOTAL_PAGES_KEY_PREFIX = 'homeCarousel:totalPages';

// `querySignature` là chữ ký của câu query (xem queryKey trong HomePage/saga), KHÔNG
// phải section.key: prefetch chạy trước khi widget config về nên chưa biết key.
export const getTotalPagesStorageKey = (region: string, querySignature: string) =>
  `${TOTAL_PAGES_KEY_PREFIX}:${region}:${querySignature}`;

export const getCachedTotalPages = (storageKey: string): number => {
  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) return 0;

    const parsed = Number(raw);

    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
  } catch {
    return 0;
  }
};

export const setCachedTotalPages = (storageKey: string, totalPages: number) => {
  try {
    if (totalPages > 0) {
      window.localStorage.setItem(storageKey, String(totalPages));
    }
  } catch {
    // ignore quota / private mode
  }
};

export const getSeededBasePage = (seed: number, totalPages: number) => {
  const pages = Math.max(1, totalPages);

  return seed % pages;
};

// Page kế tiếp khi load-more, chạy vòng về 0 sau page cuối để section bốc trúng page cuối
// vẫn còn dữ liệu để tải thêm. Việc dừng do `pagesFetched` quyết định, không phải do hết page.
export const getNextPage = (currentPage: number, totalPages: number) => {
  const pages = Math.max(1, totalPages);

  return (Math.max(0, currentPage) + 1) % pages;
};

export const calcTotalPages = (totalResults: number, pageSize: number = INITIAL_PAGE_SIZE) =>
  Math.max(1, Math.ceil(Math.max(0, totalResults) / pageSize));

export const trimToFullPages = <T>(items: T[], pageSize: number): T[] => {
  if (!items?.length || pageSize <= 0) return items || [];

  if (items.length <= pageSize) return items;

  const fullCount = Math.floor(items.length / pageSize) * pageSize;

  return fullCount > 0 ? items.slice(0, fullCount) : items;
};

const FIRST_SHOWCASE_HINT_KEY_PREFIX = 'homeCarousel:firstShowcase';

export interface IFirstShowcaseHint {
  key: string;
  sort: ProductSort[];
  filter: ProductFilter;
}

// `prefetchFirstShowcase` (HomePage/saga.ts) bắn TRƯỚC khi widgets về nên không thể
// biết widget đầu tiên là widget nào — nó đoán bằng cấu hình đã ghi lại ở LẦN TẢI
// TRANG GẦN NHẤT. Đoán sai (BE đổi thứ tự widget) thì `sameQuery` tự phát hiện lệch,
// rơi về fetch thật, và `loadShowcaseItems` sẽ ghi đè hint đúng cho lần sau — tự lành,
// cùng triết lý với cache `totalPages` ở trên. Khoá theo region, KHÔNG theo lang: widget
// nào hiển thị là quyết định của BE theo region (`widgetsRedesign(regions: [String])`
// không nhận lang), chỉ text hiển thị mới đổi theo ngôn ngữ.
export const getFirstShowcaseHintKey = (region: string) => `${FIRST_SHOWCASE_HINT_KEY_PREFIX}:${region}`;

export const getCachedFirstShowcaseHint = (storageKey: string): IFirstShowcaseHint | null => {
  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed.key !== 'string' || !Array.isArray(parsed.sort)) return null;

    return parsed as IFirstShowcaseHint;
  } catch {
    return null;
  }
};

export const setCachedFirstShowcaseHint = (storageKey: string, hint: IFirstShowcaseHint) => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(hint));
  } catch {
    // ignore quota / private mode
  }
};

export const LOAD_MORE_PREFETCH_GROUPS = 2;

const END_SNAP_EPSILON_PX = 16;

export const dropDegenerateEndSnap = (swiper: Swiper) => {
  if (swiper.destroyed) return;

  const grid = swiper.snapGrid;
  const last = grid.length - 1;

  if (last <= 0 || grid[last] - grid[last - 1] > END_SNAP_EPSILON_PX) return;

  grid.pop();

  const endSnap = grid[grid.length - 1];

  if (!swiper.animating && -swiper.translate > endSnap) {
    swiper.setTranslate(-endSnap);
  }

  swiper.updateProgress();
  swiper.navigation?.update();
};

export const getProductSlidesPerGroup = (showcaseId: string, width: number) => {
  if (width < 768) {
    return 2;
  }

  if (showcaseId === 'BEST_PRICES') {
    return 4;
  }

  return 5;
};
